import { createApp, reactive, onBeforeUnmount, h } from 'vue';
import { useExpose } from './hooks';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

/**
 * createApp的封装，提供了事件监听、props修改、声明周期钩子。
 * @param component 组件
 * @param options.appendToBody {boolean} 是否将组件dom挂载到body上
 * @param options.propsData {Record<string, unknown>} props数据
 * @param options.listeners {Record<string, unknown>} 事件监听
 * @param options.lifecycles {{ onBeforeUnmount?: () => unknown }} 是否将组件dom挂载到body上
 * @returns 返回对象{ app, vm, clean, updatePropsData }
 *
 * 如果传了 appendToBody 可通过 clean 卸载dom
 *
 * 可通过 updatePropsData 更新props
 */
function createMountedApp(component) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var _options$propsData = options.propsData,
    propsData = _options$propsData === void 0 ? {} : _options$propsData,
    _options$listeners = options.listeners,
    listeners = _options$listeners === void 0 ? {} : _options$listeners,
    _options$lifecycles = options.lifecycles,
    lifecycles = _options$lifecycles === void 0 ? {} : _options$lifecycles,
    appendToBody = options.appendToBody;
  var app = createApp({
    setup: function setup() {
      var state = reactive({
        propsData: propsData
      });
      useExpose({
        $updatePropsData: function $updatePropsData(obj) {
          Object.keys(obj).forEach(function (key) {
            state.propsData[key] = obj[key];
          });
        },
        // 通过vm.$propsData获取props数据
        $propsData: state.propsData
      });
      onBeforeUnmount(function () {
        var _lifecycles$onBeforeU;
        (_lifecycles$onBeforeU = lifecycles.onBeforeUnmount) === null || _lifecycles$onBeforeU === void 0 ? void 0 : _lifecycles$onBeforeU.call(lifecycles);
      });
      var events = {};
      Object.keys(listeners).forEach(function (key) {
        var eventKey = 'on' + key[0].toUpperCase() + key.slice(1);
        events[eventKey] = listeners[key];
      });
      return function () {
        return h(component, _objectSpread2(_objectSpread2({
          modelValue: true
        }, state.propsData), events));
      };
    }
  });
  var vm = app.mount(document.createElement('div'));
  if (appendToBody) {
    document.body.appendChild(vm.$el);
  }
  return {
    app: app,
    vm: vm,
    /**
     * 从body移除dom（如果指定了appendToBody）
     */
    clean: function clean() {
      var parent = vm.$el.parentNode;
      if (parent) {
        parent.removeChild(vm.$el);
      }
    },
    /**
     * 更新propsData
     * @param data 新props数据
     */
    updatePropsData: function updatePropsData(data) {
      vm.$updatePropsData(data);
    }
  };
}

export { createMountedApp as default };
